// Mobile app wizard - shared client primitives: API calls, per-step save
// state, locale text rows, asset upload with hard client-side validation
// (exact pixels, PNG-only icon, alpha scan). Steps live in steps-*.tsx.
import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiErrorCode, Locale } from "../../../shared/dto";
import type {
  AppAssetSlot,
  AppWizardResponse,
  LocaleTextMap
} from "../../../shared/app-wizard";
import { APP_LANGUAGE_FLAGS, appAssetSlotSpec } from "../../../shared/app-wizard";
import { VoiceableInput } from "../../VoiceableInput";
import type { ActionIssue } from "../../action-error";

export type WizardT = (key: string) => string;

export interface StepProps {
  data: AppWizardResponse;
  locale: Locale;
  csrfToken: string;
  readOnly: boolean;
  t: WizardT;
  /** Persist one step payload; resolves with fresh derived state or null on error. */
  save: (step: string, payload: Record<string, unknown>) => Promise<AppWizardResponse | null>;
  onOpenStep: (step: string) => void;
  /** S21 chain state: the Apple checklist really went through (apple_account resource). */
  appleConfirmed: boolean;
  /** The existing honest S21 checklist, rendered by AppTrack (step 4 hosts it). */
  appleChecklist?: React.ReactNode;
  /** Review step: submit the whole wizard; resolves ok. */
  onSubmitWizard?: () => Promise<ActionIssue | null>;
  submitting?: boolean;
}

// ── API ───────────────────────────────────────────────────────────────────────

export interface WizardLoadFailure {
  status: number;
  code: ApiErrorCode | "network_error";
}

export type WizardLoadResult =
  | { ok: true; data: AppWizardResponse }
  | { ok: false; failure: WizardLoadFailure };

function wizardTestQuery(): string {
  try {
    return window.sessionStorage.getItem("launchpad-test-mode") === "1" ? "?test=1" : "";
  } catch {
    return "";
  }
}

export async function fetchWizard(signal?: AbortSignal): Promise<WizardLoadResult> {
  try {
    const response = await fetch(`/api/app/wizard${wizardTestQuery()}`, {
      credentials: "include",
      signal
    });
    const body = await response.json() as AppWizardResponse | { ok: false; error?: ApiErrorCode };
    if (response.ok && body.ok) return { ok: true, data: body };
    return {
      ok: false,
      failure: {
        status: response.status,
        code: "error" in body && body.error ? body.error : "internal_error"
      }
    };
  } catch {
    return { ok: false, failure: { status: 0, code: "network_error" } };
  }
}

export async function putWizardStep(
  step: string,
  payload: Record<string, unknown>,
  csrfToken: string
): Promise<AppWizardResponse | null> {
  try {
    const response = await fetch(`/api/app/wizard/steps/${step}${wizardTestQuery()}`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Launchpad-CSRF": csrfToken,
        "Idempotency-Key": crypto.randomUUID()
      },
      body: JSON.stringify(payload)
    });
    const body = await response.json() as AppWizardResponse | { ok: false };
    return response.ok && body.ok ? body : null;
  } catch {
    return null;
  }
}

// ── Deep-link focus (Apple banner CTA opens the exact step) ───────────────────

const FOCUS_KEY = "launchpad-app-wizard-focus";

export function requestWizardFocus(step: string): void {
  try { window.sessionStorage.setItem(FOCUS_KEY, step); } catch { /* private mode */ }
}

export function consumeWizardFocus(): string | null {
  try {
    const value = window.sessionStorage.getItem(FOCUS_KEY);
    if (value) window.sessionStorage.removeItem(FOCUS_KEY);
    return value;
  } catch {
    return null;
  }
}

// ── Debounced step save with a visible state ──────────────────────────────────

export type SaveState = "idle" | "saving" | "saved" | "error";

/**
 * One saver per step: call `queue(payload)` on every edit; the payload is
 * PUT after a short pause, in-place, no reloads. `flush` forces the write
 * (used before leaving the step so nothing is lost).
 */
export function useStepSaver(
  step: string,
  save: StepProps["save"],
  readOnly: boolean
): { state: SaveState; queue: (payload: Record<string, unknown>) => void; flush: () => void } {
  const [state, setState] = useState<SaveState>("idle");
  const pendingRef = useRef<Record<string, unknown> | null>(null);
  const timerRef = useRef(0);

  const write = useCallback(() => {
    const payload = pendingRef.current;
    if (!payload) return;
    pendingRef.current = null;
    setState("saving");
    void save(step, payload).then((result) => {
      // A newer payload queued while this one was in flight keeps "saving".
      if (pendingRef.current) return;
      setState(result ? "saved" : "error");
      if (result) window.setTimeout(() => setState((current) => (current === "saved" ? "idle" : current)), 2000);
    });
  }, [save, step]);

  const queue = useCallback((payload: Record<string, unknown>) => {
    if (readOnly) return;
    pendingRef.current = payload;
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(write, 900);
  }, [readOnly, write]);

  const flush = useCallback(() => {
    window.clearTimeout(timerRef.current);
    write();
  }, [write]);

  useEffect(() => () => {
    // Unmount (step switch, section close): never lose a queued edit.
    window.clearTimeout(timerRef.current);
    write();
  }, [write]);

  return { state, queue, flush };
}

export function SaveBadge({ state, t }: { state: SaveState; t: WizardT }) {
  if (state === "idle") return null;
  return (
    <span className={`appwiz-save-badge status-pill ${state === "saved" ? "pill-done" : state === "saving" ? "pill-team" : "pill-locked"}`} role="status">
      {t(`appWizard.save.${state}`)}
    </span>
  );
}

// ── Field primitives ──────────────────────────────────────────────────────────

export function FieldBlock(props: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="appwiz-field">
      <span className="appwiz-field-label">{props.label}</span>
      {props.hint && <p className="appwiz-field-hint">{props.hint}</p>}
      {props.children}
    </div>
  );
}

export function CharCount({ value, max }: { value: string; max: number }) {
  return <span className={`appwiz-charcount${value.length >= max ? " full" : ""}`}>{value.length}/{max}</span>;
}

/** Human name of a language/country in the interface locale, code fallback. */
export function displayName(locale: Locale, kind: "language" | "region", code: string): string {
  try {
    const names = new Intl.DisplayNames([locale === "cs" ? "cs" : "en"], { type: kind });
    return names.of(kind === "region" ? code.toUpperCase() : code) ?? code;
  } catch {
    return code;
  }
}

export function languageFlag(code: string): string {
  return APP_LANGUAGE_FLAGS[code] ?? "";
}

/**
 * Per-locale text rows for store copy. Long fields render the voice-first
 * input (mic + textarea, same as questionnaires); short ones a plain input
 * with a live character count.
 */
export function LocaleTextRows(props: {
  locales: string[];
  value: LocaleTextMap;
  max: number;
  multiline?: boolean;
  voice?: boolean;
  csrfToken: string;
  readOnly: boolean;
  ariaLabel: string;
  t: WizardT;
  onChange: (next: LocaleTextMap) => void;
}) {
  const setLocale = (locale: string, text: string) => {
    props.onChange({ ...props.value, [locale]: text.slice(0, props.max) });
  };
  return (
    <div className="appwiz-locale-rows">
      {props.locales.map((locale) => {
        const text = props.value[locale] ?? "";
        return (
          <div className="appwiz-locale-row" key={locale}>
            <span className="appwiz-locale-chip" aria-hidden="true">{languageFlag(locale)} {locale}</span>
            {props.voice && !props.readOnly ? (
              <div className="appwiz-locale-voice">
                <VoiceableInput
                  scope="app_voice"
                  value={text}
                  csrfToken={props.csrfToken}
                  mini
                  ariaLabel={`${props.ariaLabel} (${locale})`}
                  t={props.t}
                  onTextChange={(next) => setLocale(locale, next)}
                  onAttachmentReady={() => undefined}
                />
                <CharCount value={text} max={props.max} />
              </div>
            ) : props.multiline ? (
              <div className="appwiz-locale-voice">
                <textarea
                  value={text}
                  rows={3}
                  readOnly={props.readOnly}
                  aria-label={`${props.ariaLabel} (${locale})`}
                  onChange={(event) => setLocale(locale, event.target.value)}
                />
                <CharCount value={text} max={props.max} />
              </div>
            ) : (
              <span className="appwiz-locale-input">
                <input
                  type="text"
                  value={text}
                  readOnly={props.readOnly}
                  aria-label={`${props.ariaLabel} (${locale})`}
                  onChange={(event) => setLocale(locale, event.target.value)}
                />
                <CharCount value={text} max={props.max} />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Asset upload with hard validation ─────────────────────────────────────────

type UploadPhase = "idle" | "checking" | "uploading";

/**
 * Client-side gate before any byte leaves the browser: exact pixel size per
 * slot, PNG-only where required, and for the icon a real alpha scan
 * (the stores reject transparent icons; a colorType check is not enough).
 * The server re-validates dimensions from the file header.
 */
async function validateAssetFile(slot: AppAssetSlot, file: File): Promise<string | null> {
  const spec = appAssetSlotSpec(slot);
  if (!spec) return "appWizard.assets.errorUnknown";
  if (!spec.contentTypes.includes(file.type)) return "appWizard.assets.errorType";
  if (file.size > spec.maxBytes) return "appWizard.assets.errorSize";
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return "appWizard.assets.errorType";
  }
  try {
    if (bitmap.width !== spec.width || bitmap.height !== spec.height) {
      return "appWizard.assets.errorDimensions";
    }
    if (slot === "icon") {
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (context) {
        context.drawImage(bitmap, 0, 0);
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        for (let index = 3; index < pixels.length; index += 4) {
          if (pixels[index] < 255) return "appWizard.assets.errorTransparent";
        }
      }
    }
  } finally {
    bitmap.close();
  }
  return null;
}

export function AssetUploadZone(props: {
  slot: AppAssetSlot;
  label: string;
  attachmentId: string | null;
  csrfToken: string;
  readOnly: boolean;
  t: WizardT;
  onUploaded: (attachmentId: string) => void;
  onCleared: () => void;
}) {
  const [phase, setPhase] = useState<UploadPhase>("idle");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const spec = appAssetSlotSpec(props.slot);

  async function handleFile(file: File) {
    setError(null);
    setPhase("checking");
    const validationError = await validateAssetFile(props.slot, file);
    if (validationError) {
      setError(validationError);
      setPhase("idle");
      return;
    }
    setPhase("uploading");
    try {
      const form = new FormData();
      form.set("scope", "app_asset");
      form.set("slot", props.slot);
      form.set("file", file);
      const response = await fetch("/api/attachments", {
        method: "POST",
        credentials: "include",
        headers: { "X-Launchpad-CSRF": props.csrfToken },
        body: form
      });
      const body = await response.json() as { ok?: boolean; attachmentId?: string; error?: string };
      if (!response.ok || !body.attachmentId) {
        setError(body.error === "image_dimensions_invalid" ? "appWizard.assets.errorDimensions" : "appWizard.assets.errorUpload");
        setPhase("idle");
        return;
      }
      props.onUploaded(body.attachmentId);
      setPhase("idle");
    } catch {
      setError("appWizard.assets.errorUpload");
      setPhase("idle");
    }
  }

  const busy = phase !== "idle";
  return (
    <div className={`appwiz-asset${props.attachmentId ? " filled" : ""}`}>
      {props.attachmentId ? (
        <img className="appwiz-asset-preview" src={`/api/attachments/${props.attachmentId}`} alt={props.label} />
      ) : (
        <span className="appwiz-asset-placeholder" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path fill="currentColor" d="M19 5v14H5V5h14m0-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2Zm-4.86 8.86-3 3.87L9 13.14 6 17h12l-3.86-5.14Z"/></svg>
        </span>
      )}
      <div className="appwiz-asset-meta">
        <strong>{props.label}</strong>
        {spec && <small>{spec.width}x{spec.height} px · {spec.contentTypes.includes("image/jpeg") ? "PNG/JPG" : "PNG"}</small>}
        {!props.readOnly && (
          <span className="appwiz-asset-actions">
            <button className="text-button" type="button" disabled={busy} onClick={() => inputRef.current?.click()}>
              {busy ? props.t("appWizard.assets.uploading") : props.t(props.attachmentId ? "appWizard.assets.replace" : "appWizard.assets.choose")}
            </button>
            {props.attachmentId && (
              <button className="text-button" type="button" disabled={busy} onClick={props.onCleared}>
                {props.t("appWizard.assets.remove")}
              </button>
            )}
          </span>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={spec?.contentTypes.join(",")}
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) void handleFile(file);
        }}
      />
      {error && <p className="appwiz-asset-error" role="alert">{props.t(error)}</p>}
    </div>
  );
}
